import toast from "react-hot-toast";
import theme from "./theme";

/**
 * Toast configuration with all styling
 */
export const toastConfig = {
  position: "top-right",
  gutter: 8,
  containerClassName: "",
  containerStyle: {},
  toastOptions: {
    // Default options for all toasts
    duration: 4000,
    style: {
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.grey[200]}`,
      borderRadius: "8px",
      boxShadow:
        "0 4px 16px rgba(44,62,80,0.10), 0 1.5px 4px rgba(44,62,80,0.08)",
      padding: "12px 16px",
      fontSize: "14px",
      fontWeight: 500,
      fontFamily: theme.typography.fontFamily,
      maxWidth: "400px",
      wordBreak: "break-word",
    },
    // Success toast styling
    success: {
      duration: 3000,
      iconTheme: {
        primary: theme.palette.success.main,
        secondary: theme.palette.success.contrastText,
      },
      style: {
        background: "#f0f9f0", // Light green for better readability
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.success.main}`,
        borderLeft: `4px solid ${theme.palette.success.main}`,
      },
    },
    // Error toast styling
    error: {
      duration: 5000,
      iconTheme: {
        primary: theme.palette.error.main,
        secondary: theme.palette.error.contrastText,
      },
      style: {
        background: theme.palette.error.light,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.error.main}`,
        borderLeft: `4px solid ${theme.palette.error.main}`,
      },
    },
    // Warning toast styling (for reference - used in showToast.warning)
    warning: {
      duration: 4000,
      iconTheme: {
        primary: theme.palette.warning.main,
        secondary: theme.palette.warning.contrastText,
      },
      style: {
        background: "#fff8e1", // Keep light yellow for readability
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.warning.light}`,
        borderLeft: `4px solid ${theme.palette.warning.main}`,
        borderRadius: "8px",
        boxShadow:
          "0 4px 16px rgba(44,62,80,0.10), 0 1.5px 4px rgba(44,62,80,0.08)",
        padding: "12px 16px",
        fontSize: "14px",
        fontWeight: 500,
        fontFamily: theme.typography.fontFamily,
        maxWidth: "400px",
        wordBreak: "break-word",
      },
    },
    // Info toast styling
    loading: {
      iconTheme: {
        primary: theme.palette.info.main,
        secondary: theme.palette.info.contrastText,
      },
      style: {
        background: theme.palette.info.light,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.info.main}`,
        borderLeft: `4px solid ${theme.palette.info.main}`,
      },
    },
  },
};

/**
 * Basic toast utility functions with duplicate prevention
 */
export const showToast = {
  /**
   * Show a success toast message (prevents duplicates)
   */
  success: (message, options = {}) => {
    const toastId = options.id || `success-${message}`;
    return toast.success(message, {
      id: toastId,
      duration: options.duration || 3000,
      ...options,
    });
  },

  /**
   * Show an error toast message (prevents duplicates)
   */
  error: (message, options = {}) => {
    const toastId = options.id || `error-${message}`;
    return toast.error(message, {
      id: toastId,
      duration: options.duration || 5000,
      ...options,
    });
  },

  /**
   * Show a warning toast message (prevents duplicates)
   */
  warning: (message, options = {}) => {
    const toastId = options.id || `warning-${message}`;
    return toast(message, {
      id: toastId,
      duration: options.duration || toastConfig.toastOptions.warning.duration,
      icon: "⚠️",
      style: {
        ...toastConfig.toastOptions.warning.style,
        ...options.style,
      },
      ...options,
    });
  },

  /**
   * Show a loading toast message (prevents duplicates)
   */
  loading: (message, options = {}) => {
    const toastId = options.id || `loading-${message}`;
    return toast.loading(message, {
      id: toastId,
      ...options,
    });
  },

  /**
   * Dismiss all active toasts
   */
  dismissAll: () => {
    return toast.dismiss();
  },
};

export default showToast;
